import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Slide, Box, Chip, Tooltip } from '@mui/material';
import { WifiOff, Wifi, SignalWifiOff } from '@mui/icons-material';

/**
 * Offline Indicator Component
 *
 * Shows a banner when the user loses internet connection
 * and automatically hides when connection is restored.
 *
 * Also provides an optional persistent header indicator.
 *
 * Usage:
 *   <OfflineIndicator />
 *   <OfflineIndicator.HeaderStatus />  // For header
 */
function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOffline, setShowOffline] = useState(false);
  const [showOnline, setShowOnline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Network status: ONLINE');
      setIsOnline(true);
      setShowOffline(false);
      setShowOnline(true);

      // Hide "back online" message after 3 seconds
      setTimeout(() => {
        setShowOnline(false);
      }, 3000);
    };

    const handleOffline = () => {
      console.log('🌐 Network status: OFFLINE');
      setIsOnline(false);
      setShowOffline(true);
      setShowOnline(false);
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state and log it
    if (!navigator.onLine) {
      console.log('🌐 Initial network status: OFFLINE');
      setShowOffline(true);
    } else {
      console.log('🌐 Initial network status: ONLINE');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Persistent Top Banner (Only shows when offline) */}
      {showOffline && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            py: 0.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            zIndex: 9999,
            boxShadow: 2
          }}
        >
          <SignalWifiOff fontSize="small" />
          <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            You're offline - Some features may not work
          </Box>
        </Box>
      )}

      {/* Bottom Banner (Slides up from bottom) */}
      <Snackbar
        open={showOffline}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        sx={{
          bottom: { xs: 80, sm: 24 },
          display: { xs: 'flex', sm: 'none' } // Only show on mobile (top banner on desktop)
        }}
      >
        <Alert
          severity="error"
          icon={<WifiOff />}
          sx={{
            width: '100%',
            boxShadow: 3
          }}
        >
          No internet connection
        </Alert>
      </Snackbar>

      {/* Back Online Banner */}
      <Snackbar
        open={showOnline}
        autoHideDuration={3000}
        onClose={() => setShowOnline(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        sx={{ bottom: { xs: 80, sm: 24 } }}
      >
        <Alert
          severity="success"
          icon={<Wifi />}
          sx={{
            width: '100%',
            boxShadow: 3
          }}
        >
          Back online!
        </Alert>
      </Snackbar>
    </>
  );
}

/**
 * Header Status Indicator (Optional)
 * Shows connection status in header
 */
OfflineIndicator.HeaderStatus = function HeaderStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Only show when offline
  if (isOnline) return null;

  return (
    <Tooltip title="You're offline. Some features may not work.">
      <Chip
        icon={<WifiOff />}
        label="Offline"
        size="small"
        color="error"
        sx={{
          ml: 1,
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.7 }
          }
        }}
      />
    </Tooltip>
  );
};

export default OfflineIndicator;
